import React, { useState } from "react";
import { base44 } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar } from "lucide-react";
import { motion } from "framer-motion";

const CATEGORIES = [
  { label: "All Posts", value: "all" },
  { label: "Tips", value: "tips" },
  { label: "Trends", value: "trends" },
  { label: "Case Studies", value: "case-study" },
  { label: "Guides", value: "guides" },
  { label: "News", value: "news" },
];

export default function Blog() {
  const [category, setCategory] = useState("all");

  const { data: posts = [], isLoading } = useQuery({
    queryKey: ["blog-published"],
    queryFn: () => base44.entities.BlogPosts.filter({ status: "published" }, "-created_date", 30),
    initialData: [],
  });

  const filtered = category === "all" ? posts : posts.filter((p) => p.category === category);

  return (
    <div className="min-h-screen bg-background">
      <section className="bg-primary py-24 px-4">
        <div className="max-w-7xl mx-auto">
          <p className="font-body text-accent text-sm font-semibold uppercase tracking-[0.18em] mb-4">Insights</p>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white max-w-2xl leading-tight">
            Remodeling Blog
          </h1>
          <p className="font-body text-white/65 mt-4 max-w-xl text-lg">
            Expert advice for Baltimore homeowners from the Top-Notch team.
          </p>
        </div>
      </section>

      {/* Category filter */}
      <div className="bg-background border-b border-border sticky top-[88px] z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex gap-2 overflow-x-auto">
          {CATEGORIES.map((cat) => (
            <button
              key={cat.value}
              onClick={() => setCategory(cat.value)}
              className={`px-4 py-2 rounded-full font-body text-sm font-medium whitespace-nowrap transition-all ${
                category === cat.value
                  ? "bg-accent text-white"
                  : "bg-secondary text-muted-foreground hover:text-foreground hover:bg-muted"
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        {isLoading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-4 border-muted border-t-accent rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-center py-20 font-body text-muted-foreground">No posts in this category yet.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
            {filtered.map((post, i) => (
              <motion.div
                key={post.id}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
              >
                <Link
                  to={`/blog/${post.slug}`}
                  className="group block bg-card rounded-xl overflow-hidden border border-border hover:shadow-lg hover:border-accent/30 transition-all h-full"
                >
                  {post.featured_image ? (
                    <div className="aspect-video overflow-hidden">
                      <img loading="lazy" decoding="async" src={post.featured_image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                  ) : (
                    <div className="aspect-video bg-secondary/80 flex items-center justify-center">
                      <span className="font-body text-xs text-muted-foreground">Upload featured image</span>
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      {post.category && (
                        <span className="font-body text-xs font-semibold text-accent uppercase tracking-wide">{post.category}</span>
                      )}
                      {post.created_date && (
                        <span className="flex items-center gap-1 text-xs text-muted-foreground font-body">
                          <Calendar className="w-3 h-3" />
                          {new Date(post.created_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                        </span>
                      )}
                    </div>
                    <h2 className="font-heading text-lg font-bold text-foreground mb-3 group-hover:text-accent transition-colors leading-snug">
                      {post.title}
                    </h2>
                    {post.excerpt && (
                      <p className="font-body text-sm text-muted-foreground leading-relaxed line-clamp-3 mb-4">{post.excerpt}</p>
                    )}
                    <span className="inline-flex items-center gap-1.5 font-body text-sm font-semibold text-accent">
                      Read More <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}